import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { subscribeNewsletter } from "../lib/api";
import { NAV_LINKS } from "../data/content";

const FOOTER_COLS = [
  { title: "Shop", links: [["Daily Wellness", "/product"], ["Bundles", "/bundles"], ["How to Use", "/how-to-use"]] },
  { title: "Learn", links: [["The Science", "/science"], ["Ingredients", "/ingredients"], ["Benefits", "/benefits"]] },
  { title: "Company", links: [["Our Philosophy", "/about"], ["Journal", "/journal"], ["Reviews", "/testimonials"]] },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're in. Welcome to NARA.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-navy-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <img src="/images/brand/logo_horizontal_white.png" alt="NARA Nutrition" className="h-9 w-auto" />
            <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-xs">
              Rooted in science. Inspired by balance. Made for modern lives. Premium wellness, reframed.
            </p>
            <form onSubmit={onSubscribe} className="mt-8 flex max-w-sm" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="newsletter-email"
                className="flex-1 bg-white/5 border border-white/15 rounded-l-full px-5 py-3 text-sm placeholder:text-slate-500 focus:outline-none focus:border-sky-400"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="bg-sky-400 text-navy-900 font-semibold rounded-r-full px-6 text-sm hover:bg-sky-300 transition-colors disabled:opacity-60"
              >
                {loading ? "..." : "Join"}
              </button>
            </form>
          </div>

          {FOOTER_COLS.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <h4 className="font-heading text-sm font-semibold tracking-wide text-white mb-5">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map(([label, to]) => (
                  <li key={label}>
                    <Link to={to} className="text-sm text-slate-400 hover:text-white transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <h4 className="font-heading text-sm font-semibold tracking-wide text-white mb-5">Explore</h4>
            <ul className="space-y-3">
              {NAV_LINKS.slice(0, 4).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-slate-400 hover:text-white transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} NARA Nutrition. All rights reserved.</p>
          <div className="flex gap-6 text-xs text-slate-500">
            <span className="hover:text-slate-300 cursor-pointer">Privacy Policy</span>
            <span className="hover:text-slate-300 cursor-pointer">Terms of Service</span>
            <span className="hover:text-slate-300 cursor-pointer">Shipping</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
